import React, { useState, useEffect, useRef, useCallback } from 'react';
import './Explainability.css';

/**
 * Live Evaluation Progress
 *
 * Shows real-time evaluation progress via WebSocket, using
 * business-language milestones.  No platform internals shown.
 */
const LiveEvaluationProgress = ({
  executionId,
  wsBaseUrl,
  onComplete,
}) => {
  const [steps, setSteps] = useState([]);
  const [isComplete, setIsComplete] = useState(false);
  const [progress, setProgress] = useState(0);
  const wsRef = useRef(null);
  const containerRef = useRef(null);

  const connect = useCallback(() => {
    if (!executionId || !wsBaseUrl) return;

    const url = `${wsBaseUrl}/api/explain/ws/progress/${executionId}`;
    const ws = new WebSocket(url);
    wsRef.current = ws;

    ws.onmessage = (event) => {
      try {
        const data = JSON.parse(event.data);

        if (data.type === 'progress') {
          setSteps(prev => {
            if (prev.length > 0 && prev[prev.length - 1].message === data.message) {
              return prev;
            }
            return [...prev, {
              message: data.message,
              timestamp: new Date().toLocaleTimeString(),
              totalSteps: data.total_steps || 0,
            }];
          });
          if (data.total_steps) {
            const phases = 5;
            setProgress(Math.min((data.total_steps / (phases * 20)) * 100, 95));
          }
        }

        if (data.type === 'complete') {
          setIsComplete(true);
          setProgress(100);
          if (onComplete) onComplete();
        }
      } catch (e) {
        // ignore malformed messages
      }
    };

    ws.onerror = () => {
      // silently handle - will reconnect or show last known state
    };

    ws.onclose = () => {
      if (!isComplete) {
        setTimeout(() => connect(), 3000);
      }
    };
  }, [executionId, wsBaseUrl, isComplete, onComplete]);

  useEffect(() => {
    connect();
    return () => {
      if (wsRef.current) {
        wsRef.current.close();
      }
    };
  }, [connect]);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollTop = containerRef.current.scrollHeight;
    }
  }, [steps]);

  if (!executionId) return null;

  return (
    <div className="explain-live-progress">
      <h4 className="explain-section-title">
        {isComplete ? 'Evaluation Complete' : 'Evaluation In Progress'}
      </h4>

      <div className="explain-progress-bar-container">
        <div
          className={`explain-progress-bar ${isComplete ? 'explain-progress-bar--complete' : ''}`}
          style={{ width: `${progress}%` }}
        />
      </div>

      <div className="explain-live-steps" ref={containerRef}>
        {steps.map((step, i) => (
          <div key={i} className="explain-live-step">
            <span className="explain-live-step-time">{step.timestamp}</span>
            <span className={`explain-live-step-dot ${i === steps.length - 1 && !isComplete ? 'explain-live-step-dot--active' : ''}`} />
            <span className="explain-live-step-message">{step.message}</span>
          </div>
        ))}
        {!isComplete && steps.length > 0 && (
          <div className="explain-live-step explain-live-step--pending">
            <span className="explain-live-step-time" />
            <span className="explain-live-step-dot explain-live-step-dot--pending" />
            <span className="explain-live-step-message explain-pulse">Processing...</span>
          </div>
        )}
      </div>

      {isComplete && (
        <div className="explain-live-complete-badge">
          Evaluation completed successfully
        </div>
      )}
    </div>
  );
};

export default LiveEvaluationProgress;
